import { useState } from 'react';
import { Avatar, Box, Skeleton, Tooltip, Typography } from '@mui/material';
import EditNoteDialog from 'dashboard409a/components/EditNoteDialog';
import { updateValuationStatus } from 'dashboard409a/services/attio-common';
import { Note } from 'dashboard409a/services/models.types';
import { getInitials, Logger } from 'utilities';
import { StyledSpanEmptyStatus, StyledStatusChip, StyledTableCell, StyledTableRow } from '../common';
import { StatusDropdown } from '../StatusDropdown';
import { ValuationsTableRowProps } from './ValuationsTableRow.types';

// Helper function to truncate text
const truncateText = (text: string | null, maxLength: number = 70): string => {
  if (!text || text === '-') {
    return text || '-';
  }
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.substring(0, maxLength).trim()}...`;
};

// Helper function to render notes cell
const renderNotesCell = (valuationRow: ValuationsTableRowProps['valuationRow']) => {
  if (valuationRow.is_loading_additional_data) {
    return <Skeleton variant="text" width="80%" height={20} />;
  }

  // Use only the new note structure
  const noteContent = valuationRow.note?.content || '-';
  const hasNotes = noteContent && noteContent !== '-' && noteContent !== 'null' && noteContent !== 'undefined';
  const truncatedText = truncateText(noteContent);
  const isTruncated = hasNotes && noteContent.length > 70;
  
  if (isTruncated) {
    return (
      <Tooltip title={noteContent} placement="top" arrow>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'text.primary',
            fontStyle: 'normal'
          }}
        >
          {truncatedText}
        </Typography>
      </Tooltip>
    );
  }
  
  return (
    <Typography 
      variant="body2" 
      sx={{ 
        color: hasNotes ? 'text.primary' : 'text.secondary',
        fontStyle: hasNotes ? 'normal' : 'italic'
      }}
    >
      {truncatedText}
    </Typography>
  );
};

// Helper function to render consultant cell
const renderConsultantCell = (valuationRow: ValuationsTableRowProps['valuationRow']) => {
  if (valuationRow.is_loading_additional_data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Box>
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </Box>
    );
  }

  if (valuationRow.other_consultant && valuationRow.other_consultant !== '-') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar
          alt={valuationRow.other_consultant}
          src={
            valuationRow.other_consultant_image_src && valuationRow.other_consultant_image_src !== '-'
              ? valuationRow.other_consultant_image_src
              : undefined
          }
          sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
          {getInitials({ name: valuationRow.other_consultant })}
        </Avatar>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
            {valuationRow.other_consultant}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      -
    </Typography>
  );
};

// Helper function to render engagement manager cell
const renderEngagementManagerCell = (valuationRow: ValuationsTableRowProps['valuationRow']) => {
  if (valuationRow.is_loading_additional_data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Box>
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </Box>
    );
  }

  if (valuationRow.engagement_manager && valuationRow.engagement_manager !== '-') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar
          alt={valuationRow.engagement_manager}
          src={
            valuationRow.engagement_manager_image_src && valuationRow.engagement_manager_image_src !== '-'
              ? valuationRow.engagement_manager_image_src
              : undefined
          }
          sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
          {getInitials({ name: valuationRow.engagement_manager })}
        </Avatar>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
            {valuationRow.engagement_manager}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      -
    </Typography>
  );
};

const ValuationsTableRow = ({
  valuationRow,
  onRowClick,
  onStatusUpdate,
  onNoteUpdate,
  onNoteSave,
  statusOptions,
  statusOptionsLoading,
  isNoteLoading = false,
  noteError = null,
}: ValuationsTableRowProps) => {
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);

  // Check if status is final and should be read-only
  const isFinalStatus = valuationRow.status === 'Final Sent';

  const handleStatusChange = async (newStatus: string, statusOptionId: string) => {
    try {
      setIsUpdatingStatus(true);

      // Update status via API
      await updateValuationStatus(valuationRow.record_id, statusOptionId);

      // Update local data
      const updatedRow = { ...valuationRow, status: newStatus };

      // Notify parent component of the update
      if (onStatusUpdate) {
        onStatusUpdate(updatedRow);
      }

      Logger.info('Status successfully updated to:', newStatus);
      setIsEditingStatus(false);
    } catch (error) {
      Logger.error('Error updating status:', error);
      // Could add toast notification here for better UX
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleNoteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsNoteDialogOpen(true);
  };

  const handleNoteSave = async (newNote: string) => {
    if (onNoteSave) {
      await onNoteSave(newNote, valuationRow.note?.content || '');
    } else {
      // Fallback to old behavior
      const updatedRow = { ...valuationRow, note: { content: newNote } as Note };
      if (onNoteUpdate) {
        onNoteUpdate(updatedRow);
      }
    }
  };

  return (
    <StyledTableRow
      hover
      tabIndex={-1}
      key={valuationRow.record_id}
      style={{ background: '#ffff' }}
      onClick={event => onRowClick(valuationRow, event)}>
      <StyledTableCell align="left">{valuationRow.company_name}</StyledTableCell>
      <StyledTableCell align="left">
        <StatusDropdown
          currentStatus={valuationRow.status}
          onStatusChange={handleStatusChange}
          isEditing={isEditingStatus}
          onEditEnd={() => setIsEditingStatus(false)}
          statusOptions={statusOptions}
          loading={statusOptionsLoading}
        />

        {!isEditingStatus && (
          <>
            {valuationRow.status ? (
              <StyledStatusChip
                label={isUpdatingStatus ? 'Updating...' : valuationRow.status}
                status={valuationRow.status}
                onClick={e => {
                  e.stopPropagation();
                  if (!isUpdatingStatus && !isFinalStatus) {
                    setIsEditingStatus(true);
                  }
                }}
                style={{
                  cursor: isUpdatingStatus || isFinalStatus ? 'not-allowed' : 'pointer',
                  opacity: isUpdatingStatus ? 0.6 : 1,
                }}
                title={isFinalStatus ? 'Final status cannot be changed' : undefined}
              />
            ) : (
              <StyledSpanEmptyStatus
                onClick={e => {
                  e.stopPropagation();
                  if (!isUpdatingStatus && !isFinalStatus) {
                    setIsEditingStatus(true);
                  }
                }}
                style={{
                  cursor: isUpdatingStatus || isFinalStatus ? 'not-allowed' : 'pointer',
                  color: '#666',
                  fontStyle: 'italic',
                  opacity: isUpdatingStatus ? 0.6 : 1,
                }}>
                {isUpdatingStatus ? 'Updating...' : 'Click to select status'}
              </StyledSpanEmptyStatus>
            )}
          </>
        )}
      </StyledTableCell>
      <StyledTableCell 
        align="left" 
        onClick={handleNoteClick}
        sx={{ 
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }
        }}
      >
        {renderNotesCell(valuationRow)}
      </StyledTableCell>
      <StyledTableCell align="left">
        {renderConsultantCell(valuationRow)}
      </StyledTableCell>
      <StyledTableCell align="left">
        {renderEngagementManagerCell(valuationRow)}
      </StyledTableCell>
      <StyledTableCell align="left">{valuationRow.valuation_date}</StyledTableCell>
      <StyledTableCell align="left">{valuationRow.valuation_fee}</StyledTableCell>
      
      {/* Edit Note Dialog */}
      <EditNoteDialog
        open={isNoteDialogOpen}
        onClose={() => setIsNoteDialogOpen(false)}
        onSave={handleNoteSave}
        currentNote={valuationRow.note?.content || ''}
        companyName={valuationRow.company_name || ''}
        isLoading={isNoteLoading}
        error={noteError}
      />
    </StyledTableRow>
  );
};
export default ValuationsTableRow;
